import { styled } from '@mui/material/styles';
import { Box, Paper, Typography, TextField, Button, Chip, Avatar } from '@mui/material';

// Container styles
export const StyledContainer = styled(Box)(({ theme }) => ({
  paddingBottom: theme.spacing(6),
  paddingTop: theme.spacing(3),
}));

export const StyledPageHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  textAlign: 'center',
}));

export const StyledHeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

export const StyledTitle = styled(Typography)({
  marginBottom: 0,
});

export const StyledDescription = styled(Typography)(({ theme }) => ({
  maxWidth: 600,
  margin: '0 auto',
  fontSize: '1.1rem',
}));

// Paper styles
export const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(148, 163, 184, 0.15)',
  borderRadius: theme.spacing(3),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(148, 163, 184, 0.05) inset',
  elevation: 0,
}));

// Section header styles
export const StyledSectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

export const StyledSectionHeaderWithBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 0,
}));

export const StyledIconBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(2),
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontSize: 28,
}));

export const StyledAnalyticsIconBox = styled(Box)({
  width: 36,
  height: 36,
  borderRadius: 999,
  background: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  boxShadow: '0 4px 14px rgba(244, 63, 94, 0.35)',
  verticalAlign: 'middle',
  '& svg': {
    fontSize: 18,
    filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.25))',
  },
});

// TextField styles
export const StyledTextField = styled(TextField)({
  marginBottom: '0px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '24px',
    '& fieldset': {
      borderColor: 'rgba(148, 163, 184, 0.2)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(148, 163, 184, 0.4)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#6366f1',
    },
  },
});

// Button styles
export const StyledSubmitButton = styled(Button)(({ theme }) => ({
  borderRadius: '24px',
  padding: '12px 32px',
  fontSize: '1rem',
  fontWeight: 600,
  boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)',
  '&:hover': {
    boxShadow: '0 12px 48px rgba(99, 102, 241, 0.4)',
  },
}));

// Alert styles
export const StyledAlert = styled(Box)({
  borderRadius: '24px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
});

// Chip styles
export const StyledChannelChip = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontWeight: 600,
  fontSize: '0.9rem',
  height: 36,
}));

// Empty state styles
export const StyledEmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
}));

export const StyledEmptyStateIcon = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
  border: '2px solid rgba(148, 163, 184, 0.2)',
}));

// Card styles (using default Card component with theme styles)

export const StyledCardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

export const StyledChannelAvatar = styled(Avatar)({
  width: 48,
  height: 48,
  background: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
});

export const StyledChannelId = styled(Typography)(({ theme }) => ({
  fontFamily: 'monospace',
  color: theme.palette.primary.main,
  fontWeight: 600,
  wordBreak: 'break-word',
  fontSize: '0.85rem',
}));

export const StyledInfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const StyledInfoText = styled(Typography)({
  fontSize: '0.75rem',
});

export const StyledStack = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}));
